import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const signals = [
  {
    company: "Northstar",
    signal: "Proposal opened 3 times",
    move: "Call the buyer before noon",
    heat: "hot",
  },
  {
    company: "Acme Corp",
    signal: "No reply in 9 days",
    move: "Follow up with a relevant case study",
    heat: "risk",
  },
  {
    company: "Delta Systems",
    signal: "Main contact changed roles",
    move: "Find the new decision maker",
    heat: "watch",
  },
  {
    company: "Helix",
    signal: "Returned to your pricing page",
    move: "Ask what they need to decide",
    heat: "hot",
  },
  {
    company: "Orbit Health",
    signal: "Thread went quiet mid-negotiation",
    move: "Follow up on the open questions",
    heat: "risk",
  },
];

const heatColor = {
  hot: "bg-flame",
  risk: "bg-cobalt",
  watch: "bg-paper",
};

const heatText = {
  hot: "text-white",
  risk: "text-white",
  watch: "text-ink",
};

export const AiAgents = () => {
  const [active, setActive] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % signals.length);
    }, 2800);
    return () => clearInterval(id);
  }, [reducedMotion]);

  const ordered = [
    signals[active],
    ...signals.filter((_, i) => i !== active),
  ].slice(0, 4);

  return (
    <section
      id="agents"
      className="relative overflow-hidden bg-ink text-paper border-t border-ink"
      data-testid="ai-agents-section"
    >
      {/* atmosphere */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        aria-hidden
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-1/2 h-[120vw] w-[120vw] max-w-[900px] max-h-[900px] -translate-y-1/2 rounded-full border border-white/10"
        animate={reducedMotion ? undefined : { rotate: 360 }}
        transition={{ duration: 80, ease: "linear", repeat: Infinity }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-10 top-1/2 h-[70vw] w-[70vw] max-w-[560px] max-h-[560px] -translate-y-1/2 rounded-full border border-cobalt/40"
        animate={reducedMotion ? undefined : { rotate: -360 }}
        transition={{ duration: 55, ease: "linear", repeat: Infinity }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 font-display font-semibold leading-none text-[22vw] md:text-[16vw] text-white/[0.03] select-none tracking-[-0.06em]"
      >
        AGENTS
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start lg:items-center">
          {/* copy */}
          <div className="lg:col-span-6 xl:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="font-mono text-base md:text-lg font-semibold uppercase tracking-[0.14em] text-flame text-pretty"
            >
              AI built for your sales team
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-display font-semibold tracking-[-0.035em] text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[0.92] text-paper max-w-xl text-balance"
            >
              Stop losing deals to{" "}
              <span className="font-editorial italic font-medium text-cobalt">missed follow-ups.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-6 text-xl md:text-2xl font-light leading-relaxed text-paper max-w-lg text-pretty"
            >
              Our AI flags who needs a call and updates your CRM. Your reps know when to follow up and what to say.
            </motion.p>
          </div>

          {/* live priority board */}
          <div className="lg:col-span-6 xl:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 40, rotateX: 8 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
              style={{ perspective: 1200 }}
            >
              <div className="absolute -inset-px bg-gradient-to-br from-cobalt via-flame/80 to-transparent opacity-60 blur-[1px]" aria-hidden />
              <div className="relative border border-white/20 bg-ink overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-white/15 px-5 md:px-7 py-4">
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inset-0 animate-ping rounded-full bg-flame opacity-75" />
                      <span className="relative h-2.5 w-2.5 rounded-full bg-flame" />
                    </span>
                    <p className="font-mono text-base font-semibold uppercase tracking-[0.14em] text-paper text-pretty">
                      Who to call next
                    </p>
                  </div>
                  <p className="font-mono text-base uppercase tracking-[0.08em] text-paper text-pretty">
                    Example
                  </p>
                </div>

                <div className="relative px-3 md:px-5 py-4 md:py-5 space-y-3 min-h-[340px] md:min-h-[380px] overflow-hidden">
                  {/* Scanline sits above the cards, clipped to the board, so it never z-fights */}
                  <div
                    className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
                    aria-hidden
                  >
                    <div className="agent-scanline" />
                  </div>
                  <AnimatePresence mode="popLayout" initial={false}>
                    {ordered.map((item, index) => {
                      const isTop = index === 0;
                      return (
                        <motion.div
                          key={`${item.company}-${item.signal}`}
                          layout
                          initial={{ opacity: 0, y: 24, scale: 0.98 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
                          }}
                          exit={{ opacity: 0, y: -16, transition: { duration: 0.25 } }}
                          className={`relative z-10 border px-4 md:px-5 py-4 md:py-5 transition-colors duration-300 ${
                            isTop
                              ? "border-flame bg-paper text-ink shadow-hard"
                              : "border-white/15 bg-[#14161c] text-paper"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span
                                  className={`inline-flex items-center px-2 py-1 font-mono text-base font-semibold uppercase tracking-[0.08em] ${heatColor[item.heat]} ${heatText[item.heat]}`}
                                >
                                  {isTop ? "Now" : `P${index + 1}`}
                                </span>
                                <p
                                  className={`font-display font-semibold tracking-tight text-xl md:text-2xl leading-none ${
                                    isTop ? "text-ink" : "text-paper"
                                  }`}
                                >
                                  {item.company}
                                </p>
                              </div>
                              <p
                                className={`mt-3 text-base md:text-lg font-light leading-snug ${
                                  isTop ? "text-ink" : "text-paper"
                                }`}
                              >
                                {item.signal}
                              </p>
                              <p
                                className={`mt-2 font-mono text-base uppercase tracking-[0.08em] ${
                                  isTop ? "text-cobalt" : "text-flame"
                                }`}
                              >
                                → {item.move}
                              </p>
                            </div>
                            {isTop && (
                              <motion.span
                                initial={{ scale: 0.6, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="shrink-0 font-editorial italic text-4xl md:text-5xl leading-none text-cobalt"
                              >
                                01
                              </motion.span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

      </div>
    </section>
  );
};
